import React, {useContext, useEffect, useState} from "react";
import AppBar from "@material-ui/core/AppBar";
import {AuthContext, ErrorScreen, HeaderToolbar, Loading} from "../../components";
import Toolbar from "@material-ui/core/Toolbar";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import {makeStyles} from "@material-ui/core/styles";
import {useStore} from "../../store";
import {fetchAccount, invalidateAccount} from "../../store/actions";
import Typography from "@material-ui/core/Typography";
import FuseAnimate from "../../components/fuse-animate/fuse-animate";
import {Card} from "@material-ui/core";
import red from "@material-ui/core/colors/red";
import {AccountService} from "../../services";
import {useSnackbar} from "notistack";
import {apiError} from '../../utils/toast'
import TextField from "@material-ui/core/TextField";

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
    },
    appBar: {
        zIndex: theme.zIndex.drawer + 1,
    },
    content: {
        display       : 'flex',
        flexDirection : 'column',
        flexGrow      : 1,
        marginTop    : theme.spacing(2),
    },
    cardContent: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: 24,
        minHeight: 443,
        maxWidth: 340,
    },
    closeButton: {
        backgroundColor: red[600],
        color: "white",
        '&:focus, &:active, &:hover': {
            backgroundColor: red[600],
        },
        marginTop: 36,
        width: 200,
    },
    title: {
        marginBottom: 16,
    }
}));

const CloseAccountPage = ({location, history}) => {
    const classes = useStyles();
    const [store, dispatch] = useStore();   // Get the global store
    const [state, setState] = useState({
        stage      : 'start',
        submitting : false,
        reason     : 0,
        confirm    : '',
    })
    const {enqueueSnackbar} = useSnackbar()

    const reasons = [
        {value: 1, reason: 'My inmate has been released.'},
        {value: 2, reason: 'It wasn\'t saving me money.'},
        {value: 3, reason: 'I don\'t need it any more.'},
        {value: 4, reason: 'I\'m moving to another provider.'},
        {value: 5, reason: 'I\'d rather not say.'},
    ]

    // Scroll to top on mount
    useEffect( () => {
        document.getElementById('psb').scrollTo(0,0);
    }, [])  // <- empty deps means it runs only on load

    // Make sure the account is available in the store
    useEffect(()=> {
        if (store.user.accountId) {
            dispatch(fetchAccount(store.user.accountId, store, dispatch))
        }
    }, [store.user.accountId, store, dispatch])


    // store.account.property = 'prisonconnect'
    // store.account.type="post-paid"

    const lostMinutes = minutesRemaining(store.account)

    const handleInputChange = (event) => {
        setState({
            ...state,
            [event.target.name]: event.target.value,
        })
    }

    const handleCloseButtonClick = (event) => {
        event.preventDefault();
        setState({...state, submitting: true})

        AccountService.close(store.account.id, reasons[state.reason-1].reason)
            .then(() => {
                dispatch(invalidateAccount())   // reload the account
                setState({...state, submitting: false, stage: 'complete'})
            })
            .catch(error => {
                apiError("Sorry, we can't close your account right now.", error, enqueueSnackbar);
                setState({...state, submitting: false})
            })
    }

    const handleTextChange = (event) => {
        setState({
            ...state,
            [event.target.name]: event.target.value.toLowerCase(),
        })
    }

    return (
        <div className={classes.root}>
            <AppBar position="fixed" className={classes.appBar}>
                <HeaderToolbar location={location.pathname}/>
            </AppBar>
            <Toolbar />

            <FuseAnimate animation="transition.expandIn" delay={200}>
                <main className={classes.content}>
                    { store.account.error && <div className="my-auto pb-24"><ErrorScreen error={store.account.error}/></div> }
                    { store.account.isFetching ? <div className="my-auto pb-24"><Loading/></div> : null }
                    { store.account.error === null && !store.account.isFetching ?
                        <div className="flex flex-col flex-grow">
                            <Card>
                                <div className="p-4 flex flex-col">

                                    {state.stage === 'start' ?
                                        <>
                                            <Typography variant="h5" color="error" className={classes.title}>Close Account</Typography>
                                            <Typography paragraph>
                                                We're sorry to see you go!
                                            </Typography>
                                            <Typography paragraph>
                                                Please share the reason you're closing your account today.
                                            </Typography>

                                            <ReasonSelect reason={state.reason} onChange={handleInputChange} />

                                            {lostMinutes > 0 ?
                                                <div className="mt-8">
                                                    <Typography variant="h6" color="error">Warning</Typography>
                                                    <Typography paragraph><strong>{lostMinutes} unused call minutes will be lost!</strong></Typography>
                                                    <Typography paragraph>
                                                        Type <strong>"close"</strong> on the line below to show you
                                                        really want to close this account and lose {lostMinutes} call minutes.
                                                    </Typography>
                                                    <TextField
                                                        value={state.confirm}
                                                        name="confirm"
                                                        onChange={handleTextChange}
                                                        placeholder="Type 'close' here"
                                                    />
                                                </div>
                                                : null
                                            }

                                            <Button
                                                variant="contained"
                                                className={classes.closeButton}
                                                onClick={handleCloseButtonClick}
                                                disabled={state.submitting || state.reason === 0 || (lostMinutes > 0 && state.confirm !== 'close')}
                                            >
                                                { state.submitting ? <CircularProgress size={24} color="inherit"/> : 'Close Account Now'}
                                            </Button>
                                        </>
                                    : null
                                    }

                                    {state.stage === 'complete' ?
                                        <CloseText account={store.account} history={history}/>
                                        : null
                                    }

                                </div>
                            </Card>
                        </div>
                        : null
                    }
                </main>
            </FuseAnimate>
        </div>
    )
}


const ReasonSelect = ({reason, onChange}) => {
    return (
        <Select name="reason" value={reason} onChange={onChange}>
            <MenuItem key={0} value={0} disabled>Please choose an option...</MenuItem>
            <MenuItem key={1} value={1}>My inmate has been released</MenuItem>
            <MenuItem key={2} value={2}>It wasn't saving me money.</MenuItem>
            <MenuItem key={3} value={3}>I don't need it any more.</MenuItem>
            <MenuItem key={4} value={4}>I'm moving to another provider.</MenuItem>
            <MenuItem key={5} value={5}>I'd rather not say.</MenuItem>
        </Select>
    )
}


const CloseText = ({account, history}) => {
    const classes = useStyles();
    const auth = useContext(AuthContext)

    const handleLogoutButtonClick = (event) => {
        event.preventDefault();
        auth.logout();
        history.push('/');
    }

    //console.log('account=',account)
    return (
        <>
            <Typography variant="h5" color="secondary" className={classes.title}>Account Closed</Typography>

            <FuseAnimate animation="transition.expandIn" delay={100}>
                <Typography paragraph>
                    Thank you, your account has been closed.
                </Typography>
            </FuseAnimate>

            {/*<FuseAnimate animation="transition.expandIn" delay={200}>*/}
            {/*    <Typography paragraph>*/}
            {/*        Our team will finalise your account within one business day.*/}
            {/*    </Typography>*/}
            {/*</FuseAnimate>*/}

            { account.type === 'post-paid' ?
                <>
                    <FuseAnimate animation="transition.expandIn" delay={200}>
                        <Typography paragraph>
                            As part of the finalization process, we'll charge any unbilled calls to your credit card
                            and email you a final receipt.
                        </Typography>
                    </FuseAnimate>
                </>
                : null
            }
            { account.type === 'pre-paid' ?
                <>
                    { account.balance > 0 ?
                        <FuseAnimate animation="transition.expandIn" delay={200}>
                            <Typography paragraph component="div">
                                As part of the finalization process, your balance
                                of ${(account.balance/100).toFixed(2)} will be refunded to your card.
                                <div className="pt-4">
                                    We'll email a receipt when the refund has been issued and you will normally
                                    see the money in your account 2-3 business days after that.
                                </div>
                            </Typography>
                        </FuseAnimate>
                        : null
                    }
                </>
                : null
            }

            <FuseAnimate animation="transition.expandIn" delay={300}>
                <Typography paragraph>
                    Thanks for being a customer and bye for now!
                </Typography>
            </FuseAnimate>

            <FuseAnimate animation="transition.expandIn" delay={400}>
                <Button variant="contained" color="secondary" onClick={handleLogoutButtonClick}>
                    Log Out
                </Button>
            </FuseAnimate>
        </>
    )

}

const minutesRemaining = (account) => {
    if (!account) {
        return 0;
    }
    if (!account.services) {
        return 0;
    }
    let minutes = 0;
    for (let i=0; i < account.services.length; i++) {
        minutes += account.services[i].callCredits;
    }
    return minutes;
}

export default CloseAccountPage;